import client from './client'
import { ListParams } from '../types/index'

// ---------------------------------------------------------------------------
// Paging helpers (WI #337)
//
// Every list endpoint on the backend takes `PaginationParams`
// (`backend/app/schemas/common.py`) and binds it as **snake_case** query
// params — `page_size`, `sort_by`, `sort_order`. Nothing in the stack converts
// case for us, and FastAPI silently ignores query params it does not
// recognise, so a camelCase `pageSize` is not an error: the server just
// applies its own default of 20 and the caller never finds out.
//
// That trap had already been hit four times (#146, #322, #328, #331) and was
// still live for `ListParams` on `users` / `projects` / `tapApplications` /
// `accessGroups`. Keeping the translation in one module means a new API
// module cannot reintroduce it by forgetting to write its own mapper.
// ---------------------------------------------------------------------------

/** `PaginationParams.page_size` is validated `ge=1, le=100` — asking for more is a 422. */
export const MAX_PAGE_SIZE = 100

/**
 * Safety valve for {@link fetchAllPages}: 25 pages × 100 rows = 2,500 rows.
 * Far above any per-application collection, but bounded so a backend bug
 * cannot spin the browser forever.
 */
export const MAX_PAGES = 25

/** Default rows per page for the list views. Matches the backend default. */
export const DEFAULT_PAGE_SIZE = 20

/**
 * Translate the frontend's camelCase `ListParams` into the snake_case query
 * params the backend actually binds. Returns `undefined` for no params so
 * axios omits the query string entirely.
 */
export const toRawListParams = <T extends ListParams>(
  params?: T,
): Record<string, unknown> | undefined => {
  if (!params) return undefined
  const raw: Record<string, unknown> = {}
  if (params.page !== undefined) raw.page = params.page
  if (params.pageSize !== undefined) raw.page_size = params.pageSize
  if (params.search !== undefined) raw.search = params.search
  if (params.sortBy !== undefined) raw.sort_by = params.sortBy
  if (params.sortOrder !== undefined) raw.sort_order = params.sortOrder
  if (params.isActive !== undefined) raw.is_active = params.isActive
  if (params.includeInactive !== undefined) raw.include_inactive = params.includeInactive
  return raw
}

interface PageEnvelope<T> {
  items?: T[]
  total?: number
}

/**
 * GET every page of a paginated collection and return the rows as one array.
 *
 * For collections that belong to a single parent record — an application's
 * emails, notes, interviews, documents — pagination is an implementation
 * detail of the endpoint, not something the UI wants to expose. Those tabs
 * previously called the endpoint with no params at all and rendered whatever
 * came back, which was the first 20 rows; because `unwrapList` threw `total`
 * away, neither the component nor the user could tell the list was cut off.
 *
 * Tolerates an unpaginated endpoint: if the response is a bare array we return
 * it and make no further requests.
 *
 * If the safety limit is reached before the set is complete we log rather than
 * truncate silently — a partial list that looks complete is the whole reason
 * WI #337 exists.
 */
export const fetchAllPages = async <T>(
  url: string,
  params?: Record<string, unknown>,
): Promise<T[]> => {
  const collected: T[] = []
  let total: number | undefined

  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const response = await client.get<PageEnvelope<T> | T[]>(url, {
      params: { ...(params || {}), page, page_size: MAX_PAGE_SIZE },
    })
    const data = response.data

    // Unpaginated endpoint — one request is the whole answer.
    if (Array.isArray(data)) return data

    const items = Array.isArray(data?.items) ? data.items : []
    collected.push(...items)
    total = typeof data?.total === 'number' ? data.total : undefined

    if (items.length === 0) break
    if (total === undefined || collected.length >= total) break
  }

  if (total !== undefined && collected.length < total) {
    console.warn(
      `[paging] ${url}: returned ${collected.length} of ${total} rows — stopped at the ` +
        `${MAX_PAGES}-page safety limit. The list on screen is incomplete.`,
    )
  }

  return collected
}
